// Follow-ups due — the reason "check back later" replies have any value.
// Surfaces real tasks bucketed by due date so a task due Oct 1 shows ON Oct 1,
// not after. Fetches real data (the old stub rendered a hardcoded empty list).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getTasks, completeTask, approveDraft, dismissDraft } from '../../api/communications';
import './DashboardTasks.css';

const FLAG_LABEL = {
  contacted_since_scheduled: 'They were contacted since this was scheduled — review the thread',
  no_thread_to_reference: 'No prior thread to reference — reach out manually',
  ai_unavailable: 'Draft could not be generated — reach out manually',
  no_payload: 'Not enough context to draft — reach out manually',
};

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

function bucketOf(due, now) {
  if (!due) return 'nodate';
  const d = startOfDay(due);
  const today = startOfDay(now);
  const endWeek = new Date(today);
  endWeek.setDate(today.getDate() + (7 - today.getDay()));
  if (d < today) return 'overdue';
  if (d.getTime() === today.getTime()) return 'today';
  if (d <= endWeek) return 'week';
  return 'later';
}

const SECTIONS = [
  { key: 'overdue', label: 'Overdue', tone: 'neg' },
  { key: 'today', label: 'Today', tone: 'accent' },
  { key: 'week', label: 'This week', tone: 'warn' },
  { key: 'later', label: 'Later', tone: '' },
  { key: 'nodate', label: 'No date', tone: '' },
];

export default function DashboardTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({}); // taskId -> edited draft body

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getTasks();
      setTasks(rows.filter((t) => t.status === 'pending'));
      setError('');
    } catch (e) {
      // Surface the failure — never render a silent empty (that reads as
      // "nothing due" when the fetch actually failed).
      setError('Could not load tasks.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markDone = async (t) => {
    setBusyId(t.id);
    try {
      await completeTask(t.id);
      setTasks((prev) => prev.filter((x) => x.id !== t.id));
      toast.success('Done');
    } catch {
      toast.error('Could not complete');
    } finally {
      setBusyId(null);
    }
  };

  const openRelated = (t) => {
    if (t.customer) navigate(`/customers/${t.customer}`);
    else if (t.lead) navigate(`/leads/${t.lead}`);
  };

  const draftBody = (t) => (t.id in drafts ? drafts[t.id] : (t.followup?.draft_body || ''));

  // One-click: send the pre-generated (or edited) draft, then close the task.
  const approveAndSend = async (t) => {
    const body = draftBody(t).trim();
    if (!body) { toast.info('Nothing to send'); return; }
    setBusyId(t.id);
    try {
      await approveDraft(t.followup.draft_id, body);
      await completeTask(t.id);
      setTasks((prev) => prev.filter((x) => x.id !== t.id));
      toast.success('Sent');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send');
    } finally {
      setBusyId(null);
    }
  };

  // Drop the draft but keep the task as a plain reminder to act by hand.
  const discardDraft = async (t) => {
    setBusyId(t.id);
    try {
      await dismissDraft(t.followup.draft_id);
      setTasks((prev) => prev.map((x) => (
        x.id === t.id ? { ...x, followup: { ...x.followup, state: 'dismissed', draft_id: null, draft_body: '' } } : x
      )));
      toast.success('Draft discarded — task kept as a reminder');
    } catch {
      toast.error('Could not discard');
    } finally {
      setBusyId(null);
    }
  };

  const now = new Date();
  const grouped = {};
  for (const t of tasks) {
    const b = bucketOf(t.due_date, now);
    (grouped[b] = grouped[b] || []).push(t);
  }
  Object.values(grouped).forEach((arr) =>
    arr.sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0)));

  return (
    <div className="fud-page">
      <div className="fud-head">
        <h2>Follow-ups due</h2>
        <p>{tasks.length} open · reach out on the day, not after</p>
      </div>

      {error ? (
        <div className="fud-error">
          {error} <button className="fud-retry" onClick={load}>Retry</button>
        </div>
      ) : null}

      {loading ? (
        <div className="fud-empty">Loading…</div>
      ) : tasks.length === 0 && !error ? (
        <div className="fud-empty">Nothing due right now. New follow-ups appear here.</div>
      ) : (
        SECTIONS.map((s) => (grouped[s.key]?.length ? (
          <section key={s.key} className="fud-section">
            <div className={`fud-section-head fud-${s.tone}`}>
              {s.label} <span className="fud-count">{grouped[s.key].length}</span>
            </div>
            {grouped[s.key].map((t) => {
              const fu = t.followup;
              const hasDraft = fu?.state === 'drafted' && fu?.draft_id;
              const flag = fu?.state === 'needs_review' ? (FLAG_LABEL[fu.flag] || 'Needs a look before reaching out') : null;
              return (
                <div key={t.id} className="fud-item">
                  <div className="fud-row">
                    <div className="fud-main">
                      <button className="fud-who" onClick={() => openRelated(t)}>
                        {t.related_label || t.title || 'Task'}
                      </button>
                      <div className="fud-title">{t.title}</div>
                      <div className="fud-meta">
                        {t.contact_phone ? <span className="fud-mono">{t.contact_phone}</span> : null}
                        {t.due_date ? <span>· due {new Date(t.due_date).toLocaleDateString()}</span> : null}
                        {hasDraft ? <span className="fud-chip fud-chip-ready">draft ready</span> : null}
                      </div>
                    </div>
                    <button className="fud-done" disabled={busyId === t.id} onClick={() => markDone(t)}>
                      Done
                    </button>
                  </div>

                  {flag ? <div className="fud-flag">⚠ {flag}</div> : null}

                  {hasDraft ? (
                    <div className="fud-draft">
                      <span className="fud-draft-label">Suggested follow-up — references what they said</span>
                      <textarea
                        className="fud-draft-text"
                        rows={3}
                        value={draftBody(t)}
                        disabled={busyId === t.id}
                        onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                      />
                      <div className="fud-draft-actions">
                        <button className="fud-send" disabled={busyId === t.id} onClick={() => approveAndSend(t)}>
                          Approve &amp; send
                        </button>
                        <button className="fud-discard" disabled={busyId === t.id} onClick={() => discardDraft(t)}>
                          Discard draft
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </section>
        ) : null))
      )}
    </div>
  );
}
